import { useCallback, useEffect, useMemo, useState } from 'react'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Input, NumericTextBox } from '@progress/kendo-react-inputs'

import {
  UniformAllocations,
  UniformAllowanceSummary,
  UniformGroup,
  getUniformAllocations,
  getUniformGroups,
  setUniformAllocations,
} from '../../data/uniformData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'
import { formatDate } from './UniformPage'

type GroupOption = { text: string; value: number | null }
const ALL_PEOPLE: GroupOption = { text: 'Everyone', value: null }

const cellKey = (employeeId: number, itemId: number) => `${employeeId}-${itemId}`

/**
 * Everyone against every item this allowance year: their allowance, what's
 * been delivered and is on order, and what's left. Allowances are edited in
 * place and saved together.
 */
const UniformAllocationsTab = ({ refreshKey }: { refreshKey: number }) => {
  const { showToast } = useToast()
  const [groups, setGroups] = useState<UniformGroup[]>([])
  const [group, setGroup] = useState<GroupOption>(ALL_PEOPLE)
  const [data, setData] = useState<UniformAllocations | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  // Unsaved allowances by cell; null means "back to the item's default".
  const [edits, setEdits] = useState<Record<string, number | null>>({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getUniformGroups()
      .then(setGroups)
      .catch(() => setGroups([]))
  }, [refreshKey])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setData(await getUniformAllocations(group.value ?? undefined))
      setEdits({})
    } catch (error) {
      showToast(getApiErrorDetails(error, "Allocations couldn't be loaded.").message, 'error')
    } finally {
      setLoading(false)
    }
  }, [group, showToast])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const editCount = Object.keys(edits).length

  const changeGroup = (next: GroupOption) => {
    if (editCount > 0 && !window.confirm('Discard your unsaved allowance changes?')) return
    setGroup(next)
  }

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (data?.employees ?? []).filter((e) => !term || e.employeeName.toLowerCase().includes(term))
  }, [data, search])

  const setCell = (employeeId: number, summary: UniformAllowanceSummary, value: number | null) => {
    const key = cellKey(employeeId, summary.uniformItemId)
    setEdits((prev) => {
      const next = { ...prev }
      if (value === summary.allowanceOverride) delete next[key]
      else next[key] = value
      return next
    })
  }

  const save = async () => {
    setSaving(true)
    try {
      await setUniformAllocations(
        Object.entries(edits).map(([key, quantity]) => {
          const [employeeId, uniformItemId] = key.split('-').map(Number)
          return { employeeId, uniformItemId, quantity }
        })
      )
      showToast(`${editCount} ${editCount === 1 ? 'allowance' : 'allowances'} saved`, 'success')
      await load()
    } catch (error) {
      showToast(getApiErrorDetails(error, "The allowances couldn't be saved.").message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const groupOptions: GroupOption[] = [ALL_PEOPLE, ...groups.map((g) => ({ text: g.name, value: g.groupId }))]

  return (
    <section className='uni-card'>
      <div className='uni-toolbar'>
        <div className='uni-filters' style={{ alignItems: 'center' }}>
          <DropDownList
            data={groupOptions}
            textField='text'
            dataItemKey='value'
            value={group}
            onChange={(e) => changeGroup(e.value)}
            style={{ width: '220px' }}
          />
          <Input
            className='uni-search'
            placeholder='Search by name'
            value={search}
            onChange={(e) => setSearch(String(e.value ?? ''))}
          />
        </div>
        <div className='uni-actions'>
          <button type='button' className='uni-btn uni-btn--secondary' disabled={editCount === 0 || saving} onClick={() => setEdits({})}>
            Undo
          </button>
          <button type='button' className='uni-btn uni-btn--primary' disabled={editCount === 0 || saving} onClick={save}>
            {saving ? 'Saving…' : editCount > 0 ? `Save ${editCount} ${editCount === 1 ? 'change' : 'changes'}` : 'Save'}
          </button>
        </div>
      </div>

      {data && (
        <p className='uni-muted uni-alloc__legend'>
          {formatDate(data.yearStart)} to {formatDate(data.yearEnd)}. Each box is the person's allowance: leave it empty
          for the item's default, and 0 stops them requesting it. Underneath is what they can still request this year. Items
          limited to groups they aren't in show a dash - add them to the group on the Groups tab to offer it.
        </p>
      )}

      {loading || !data ? (
        <div className='uni-empty'>Loading allocations…</div>
      ) : data.items.length === 0 ? (
        <div className='uni-empty'>No active uniform items have been set up yet.</div>
      ) : shown.length === 0 ? (
        <div className='uni-empty'>No one here.</div>
      ) : (
        <div className='uni-alloc__scroll'>
          <table className='uni-table uni-alloc'>
            <thead>
              <tr>
                <th className='uni-alloc__name'>Employee</th>
                {data.items.map((item) => (
                  <th key={item.uniformItemId} className='uni-alloc__cell'>
                    {item.name}
                    <div className='uni-alloc__default'>
                      Default {item.defaultAllowance}
                      {item.limitedToGroups ? ' · groups' : ''}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((employee) => (
                <tr key={employee.employeeId}>
                  <td className='uni-alloc__name'>{employee.employeeName}</td>
                  {employee.items.map((s) => {
                    const key = cellKey(employee.employeeId, s.uniformItemId)
                    const edited = key in edits
                    const value = edited ? edits[key] : s.allowanceOverride
                    return (
                      <td
                        key={s.uniformItemId}
                        className={`uni-alloc__cell${edited ? ' uni-alloc__cell--edited' : ''}${s.isOffered ? '' : ' uni-alloc__cell--off'}`}
                      >
                        {s.inItemGroups ? (
                          <NumericTextBox
                            value={value}
                            placeholder={`${s.defaultAllowance}`}
                            min={0}
                            format='n0'
                            width={72}
                            onChange={(e) =>
                              setCell(employee.employeeId, s, e.value == null ? null : Math.max(0, Math.round(e.value)))
                            }
                          />
                        ) : (
                          <span className='uni-muted'>-</span>
                        )}
                        <div className='uni-alloc__status'>
                          {s.isOffered ? (
                            <>
                              <span className={s.available === 0 ? 'uni-left--none' : ''}>{s.available} left</span>
                              {(s.used > 0 || s.outstanding > 0) && (
                                <div>
                                  {s.used} delivered{s.outstanding > 0 ? `, ${s.outstanding} on order` : ''}
                                </div>
                              )}
                            </>
                          ) : s.inItemGroups ? (
                            'Excluded'
                          ) : (
                            'Not in its groups'
                          )}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

export default UniformAllocationsTab
